/**
 * Built-in caption-contest photo pack.
 * Assets live on the web client at /caption-images/{file}.
 * Server only stores/broadcasts the stable id.
 */
export interface CaptionImage {
  id: string;
  /** Filename under /caption-images/ */
  file: string;
  /** Short alt text for accessibility. */
  alt: string;
  /** Optional witty one-liner shown above the image (not a category prompt). */
  tease?: string;
}

export const CAPTION_IMAGES: readonly CaptionImage[] = [
  {
    id: "chicken-dog",
    file: "chicken-dog.jpg",
    alt: "A photorealistic dog with a chicken head sitting proudly on a living room couch",
    tease: "Something went wrong at the pet store",
  },
  {
    id: "goldfish-driver",
    file: "goldfish-driver.jpg",
    alt: "A goldfish wearing tiny sunglasses driving a convertible down a sunny highway",
    tease: "Rush hour, fish edition",
  },
  {
    id: "raccoon-boardroom",
    file: "raccoon-boardroom.jpg",
    alt: "Raccoons in business suits around a corporate boardroom table looking serious",
    tease: "Q3 projections: more trash",
  },
  {
    id: "cat-ceo",
    file: "cat-ceo.jpg",
    alt: "A fluffy cat in reading glasses at a CEO desk with a city skyline behind",
    tease: "The new boss has arrived",
  },
  {
    id: "squirrel-gym",
    file: "squirrel-gym.jpg",
    alt: "A squirrel bench-pressing a tiny barbell made of walnuts in a gym",
    tease: "Leg day is optional. Nut day is not.",
  },
  {
    id: "penguin-vacation",
    file: "penguin-vacation.jpg",
    alt: "A penguin in a loud Hawaiian shirt sipping a tropical drink at a beach bar",
    tease: "Finally out of the cold",
  },
  {
    id: "cow-smartphone",
    file: "cow-smartphone.jpg",
    alt: "A cow sitting in a café scrolling a smartphone with perfect posture",
    tease: "She's seen the group chat",
  },
  {
    id: "owl-bouncer",
    file: "owl-bouncer.jpg",
    alt: "A stern owl bouncer with an earpiece guarding a nightclub velvet rope",
    tease: "You're not on the list",
  },
  {
    id: "hamster-pilot",
    file: "hamster-pilot.jpg",
    alt: "A hamster in a fighter-pilot helmet at the controls of a jet cockpit",
    tease: "Top Seed",
  },
  {
    id: "frog-wedding",
    file: "frog-wedding.jpg",
    alt: "Two frogs in formal wedding attire exchanging vows in a garden ceremony",
    tease: "Ribbit takes bride",
  },
  {
    id: "llama-yoga",
    file: "llama-yoga.jpg",
    alt: "A llama yoga instructor leading a class of humans on mats in a studio",
    tease: "Namastay hydrated",
  },
  {
    id: "octopus-barista",
    file: "octopus-barista.jpg",
    alt: "An octopus barista pulling espresso shots with all eight arms in a busy café",
    tease: "Eight arms, zero patience",
  },
] as const;

export function getCaptionImage(id: string): CaptionImage | undefined {
  return CAPTION_IMAGES.find((img) => img.id === id);
}

/** Public path for a caption image asset (web client). */
export function captionImageSrc(id: string): string | null {
  const img = getCaptionImage(id);
  if (!img) return null;
  return `/caption-images/${img.file}`;
}

/** Pick a random caption image, optionally avoiding a previous id. */
export function pickRandomCaptionImage(excludeId?: string): CaptionImage {
  const list = CAPTION_IMAGES;
  if (list.length === 0) {
    throw new Error("CAPTION_IMAGES is empty");
  }
  if (list.length === 1) return list[0]!;

  let pick = list[Math.floor(Math.random() * list.length)]!;
  if (excludeId && list.length > 1) {
    let guard = 0;
    while (pick.id === excludeId && guard < 20) {
      pick = list[Math.floor(Math.random() * list.length)]!;
      guard++;
    }
  }
  return pick;
}
